import type { LayoutAuthMode } from '~/composables/layout/useAppLayoutShell'
import { useAppLayoutShell } from '~/composables/layout/useAppLayoutShell'
import { useAppLayoutProfile } from '~/composables/layout/useAppLayoutProfile'
import { useAppLayoutOverlays } from '~/composables/layout/useAppLayoutOverlays'
import { useAppLayoutEditMode } from '~/composables/layout/useAppLayoutEditMode'
import { useAppLayoutToolbox } from '~/composables/layout/useAppLayoutToolbox'
import { useAppLayoutKeyboardShortcuts } from '~/composables/layout/useAppLayoutKeyboardShortcuts'
import { useEffectiveGardianRoles } from '~/composables/auth/useEffectiveGardianRoles'
import { useMe } from '~/composables/auth/useMe'

type UseDefaultLayoutParams = {
  authMode: LayoutAuthMode
}

export function useDefaultLayout(params: UseDefaultLayoutParams) {
  const { loggedIn } = useOidcAuth()
  const { roles } = useEffectiveGardianRoles()
  const { me } = useMe()

  const shell = useAppLayoutShell(() => params.authMode)
  const profile = useAppLayoutProfile()
  const toolboxOpen = ref(false)

  const overlays = useAppLayoutOverlays({
    loggedIn,
    roles,
    toolboxOpen,
    myAvatarSettings: profile.myAvatarSettings,
    myAvatarSettingsReady: profile.myAvatarSettingsReady,
    refreshMyAvatarSettings: profile.refreshMyAvatarSettings,
    updateMyAvatarSettings: profile.updateMyAvatarSettings,
    fullName: profile.fullName,
    myGraphPhotoUrl: profile.myGraphPhotoUrl,
    me,
  })

  const editMode = useAppLayoutEditMode({
    loggedIn,
    isEditModeBlockedByOnboarding: overlays.isEditModeBlockedByOnboarding,
  })

  const toolbox = useAppLayoutToolbox({
    loggedIn,
    toolboxOpen,
    isWelcomeResolved: overlays.isWelcomeResolved,
    showSeasonalWelcome: overlays.showSeasonalWelcome,
    shouldShowWelcomeSplash: overlays.shouldShowWelcomeSplash,
    trySetEditMode: editMode.trySetEditMode,
  })

  const keyboard = useAppLayoutKeyboardShortcuts({
    loggedIn,
    isConnectionRoute: toolbox.isConnectionRoute,
    isEditing: editMode.isEditing,
    trySetEditMode: editMode.trySetEditMode,
    toolboxShortcutsEnabled: overlays.toolboxShortcutsEnabled,
    onToolboxToggle: toolbox.toggleToolboxOpen,
    onGeolocate: toolbox.onToolboxGeolocate,
  })

  useHead({
    title: 'Mon Orga',
  })

  onMounted(() => {
    editMode.syncEditCheckedOnMount()
    keyboard.registerKeyboardListeners()
    overlays.initOverlayStateOnMount()
  })

  onBeforeUnmount(() => {
    keyboard.unregisterKeyboardListeners()
  })

  return {
    loggedIn,
    shell: reactive(shell),
    profile: reactive(profile),
    editMode: reactive(editMode),
    keyboard: reactive(keyboard),
    toolbox: reactive(toolbox),
    overlays: reactive(overlays),
  }
}
